/**
 * Vendor form - create + edit composer.
 *
 * Tailwind implementation of the GSTIN-first supplier composer in the
 * prototype. The persisted payload stays on the current vendor master
 * contract; bank-account edits remain a founder step-up flow.
 *
 * @see prototype/purchases/vendor-new.html - V1.1 vendor composer baseline
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  AlertCircle,
  ArrowLeft,
  Banknote,
  Check,
  ChevronDown,
  ChevronRight,
  CircleDollarSign,
  Download,
  FileCheck2,
  FileText,
  HelpCircle,
  Loader2,
  MessageCircle,
  Search,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  Upload,
  X,
} from "lucide-react";
import {
  Alert,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SwitchRow,
  cn,
} from "@flint/ui";
import { useQuery } from "@tanstack/react-query";
import { gstin, indianState, phoneIN, pin } from "@flint/api-contract";
import {
  EMPTY_FORM,
  type VendorDefaultPaymentMode,
  type VendorFormValues,
  type VendorRegistrationType,
} from "../lib/form-defaults.ts";
import { panFromGstin } from "../lib/format.ts";
import { meQueryOptions } from "../../../lib/session.js";

const optionalString = z
  .string()
  .transform((v) => v.trim())
  .refine((v) => v === "" || v.length <= 255, { message: "Too long" });

const formSchema = z.object({
  name: z.string().min(1, "Vendor name is required").max(255, "Too long"),
  contactName: optionalString,
  gstin: z
    .string()
    .transform((v) => v.trim().toUpperCase())
    .refine((v) => v === "" || gstin.safeParse(v).success, { message: "Invalid GSTIN" }),
  phone: z
    .string()
    .transform((v) => v.trim())
    .refine((v) => v === "" || phoneIN.safeParse(v).success, {
      message: "Invalid Indian mobile (10 digits, starts 6-9)",
    }),
  email: z
    .string()
    .transform((v) => v.trim())
    .refine((v) => v === "" || z.string().email().safeParse(v).success, {
      message: "Invalid email",
    }),
  addressLine1: optionalString,
  addressLine2: optionalString,
  state: z.string(),
  city: optionalString,
  pincode: z
    .string()
    .transform((v) => v.trim())
    .refine((v) => v === "" || pin.safeParse(v).success, {
      message: "Invalid 6-digit PIN",
    }),
  tdsSection: z
    .string()
    .transform((v) => v.trim().toUpperCase())
    .refine((v) => v === "" || /^[0-9]{3}[A-Z]?$/.test(v), {
      message: "TDS section like 194Q or 194C",
    }),
  isJobWorker: z.boolean(),
  paymentTerms: optionalString,

  // ── V1.1 composer extension fields (migration 0040) ─────────────
  // No string-level refinements beyond what the contract enforces on
  // submit — the contract's `vendorRegistrationType` /
  // `vendorDefaultPaymentMode` enums catch garbage values; the form
  // schema just ensures the field exists with the right shape.
  displayName: z.string().trim().max(120),
  registrationType: z.enum(["regular", "composition", "unregistered", "casual", "sez"]),
  contactRole: z.string().trim().max(32),
  phoneWhatsapp: z.boolean(),
  defaultPaymentMode: z.union([
    z.enum(["neft", "rtgs", "imps", "upi", "cheque", "cash", "card", "other"]),
    z.literal(""),
  ]),
  rcmDefault: z.boolean(),
  itcEligible: z.boolean(),
  msmeRegistered: z.boolean(),
  vendorCategory: z.string().trim().max(64),
  defaultGlAccount: z.string().trim().max(64),
  tags: z.array(z.string().trim().min(1).max(64)).max(32),
  internalNote: z.string().trim().max(2000),
});

const STATE_OPTIONS = indianState.options.map((s) => ({ value: s, label: s }));
const GST_STATE_BY_CODE: Record<string, string> = {
  "01": "Jammu and Kashmir",
  "02": "Himachal Pradesh",
  "03": "Punjab",
  "04": "Chandigarh",
  "05": "Uttarakhand",
  "06": "Haryana",
  "07": "Delhi",
  "08": "Rajasthan",
  "09": "Uttar Pradesh",
  "10": "Bihar",
  "11": "Sikkim",
  "12": "Arunachal Pradesh",
  "13": "Nagaland",
  "14": "Manipur",
  "15": "Mizoram",
  "16": "Tripura",
  "17": "Meghalaya",
  "18": "Assam",
  "19": "West Bengal",
  "20": "Jharkhand",
  "21": "Odisha",
  "22": "Chhattisgarh",
  "23": "Madhya Pradesh",
  "24": "Gujarat",
  "25": "Dadra and Nagar Haveli and Daman and Diu",
  "26": "Dadra and Nagar Haveli and Daman and Diu",
  "27": "Maharashtra",
  "28": "Andhra Pradesh",
  "29": "Karnataka",
  "30": "Goa",
  "31": "Lakshadweep",
  "32": "Kerala",
  "33": "Tamil Nadu",
  "34": "Puducherry",
  "35": "Andaman and Nicobar Islands",
  "36": "Telangana",
  "37": "Andhra Pradesh",
  "38": "Ladakh",
};

/**
 * GST registration class — values match the `vendorRegistrationType`
 * contract enum (migration 0040). Labels are operator-facing.
 */
const REGISTRATION_OPTIONS: ReadonlyArray<{
  value: VendorRegistrationType;
  label: string;
}> = [
  { value: "regular", label: "Regular" },
  { value: "composition", label: "Composition" },
  { value: "sez", label: "SEZ" },
  { value: "casual", label: "Casual taxable person" },
  { value: "unregistered", label: "Unregistered" },
];

/**
 * Contact role — free string column. Kept as a closed list in the UI for
 * tidy dropdowns, but the column accepts any varchar so a future "Other —
 * typed" branch can extend without a migration.
 */
const ROLE_OPTIONS = [
  "Account manager",
  "Sales lead",
  "Owner / founder",
  "Accountant",
  "Operations",
  "Other",
] as const;

/**
 * Default payment mode — values match `vendorDefaultPaymentMode` (same
 * shape as `vendor_payments.mode`). Labels surface the friendly
 * combinations the operator thinks in.
 */
const PAYMENT_MODE_OPTIONS: ReadonlyArray<{
  value: VendorDefaultPaymentMode;
  label: string;
}> = [
  { value: "neft", label: "NEFT" },
  { value: "rtgs", label: "RTGS" },
  { value: "imps", label: "IMPS" },
  { value: "upi", label: "UPI" },
  { value: "cheque", label: "Cheque" },
  { value: "cash", label: "Cash" },
  { value: "card", label: "Card" },
  { value: "other", label: "Other" },
];

const VENDOR_CATEGORY_OPTIONS = [
  "Raw material - general",
  "Raw material - electronics",
  "Services - professional",
  "Services - contractor",
  "Capital goods",
  "Office supplies",
  "Logistics / freight",
] as const;

const DEFAULT_GL_OPTIONS = [
  "5100 - Raw material - general",
  "5210 - Raw material - electronics",
  "6100 - Professional fees",
  "6200 - Office expense",
  "7100 - Plant & equipment",
] as const;

const TDS_OPTIONS = ["None", "194Q", "194C", "194J", "194H", "194I"] as const;

const PAYMENT_TERM_OPTIONS = [
  "Due on receipt",
  "Net 7",
  "Net 15",
  "Net 30",
  "Net 45",
  "Net 60",
] as const;

type SourceMode = "registered" | "unregistered" | "import";
type LookupStatus = "idle" | "loading" | "success" | "error";

/**
 * Where to go after a successful save — driven by which Save variant
 * the operator clicked. The parent route honors this in its `onSubmit`:
 *   - 'list'        → navigate back to /vendors (default for primary Save)
 *   - 'open'        → navigate to /vendors/{newId} (Save and open vendor)
 *   - 'add-another' → no navigation; VendorForm resets the form (create only)
 */
export type VendorSaveNext = "list" | "open" | "add-another";

export interface VendorFormProps {
  defaultValues?: VendorFormValues;
  submitLabel: string;
  pendingLabel: string;
  pending?: boolean;
  serverError?: string | null;
  onSubmit: (values: VendorFormValues, opts: { next: VendorSaveNext }) => Promise<void> | void;
  mode?: "create" | "edit";
  backHref?: string;
  recordHref?: string;
}

export function VendorForm({
  defaultValues = EMPTY_FORM,
  submitLabel,
  pendingLabel,
  pending = false,
  serverError = null,
  onSubmit,
  mode = "create",
  backHref = "/procurement/vendors",
  recordHref,
}: VendorFormProps) {
  const form = useForm<VendorFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: "onChange",
  });

  const [sourceMode, setSourceMode] = useState<SourceMode>(
    defaultValues.gstin ? "registered" : "registered",
  );
  const [lookupStatus, setLookupStatus] = useState<LookupStatus>(
    defaultValues.gstin ? "success" : "idle",
  );
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [saveSeconds, setSaveSeconds] = useState(3);
  /** Local-only draft input for the "Add tag" chip box. Committed tags
   *  live on the form (`form.getValues('tags')`); this is the typing buffer. */
  const [tagDraft, setTagDraft] = useState("");
  const [saveMenuOpen, setSaveMenuOpen] = useState(false);

  /**
   * RHF-bound setter for any VendorFormValues key. Marks the form dirty
   * + revalidates so the footer's "Ready to save" pill keeps in sync.
   *
   * The `unknown` cast is a pragmatic shortcut around RHF's
   * `Path<TFieldValues>` / `PathValue<...>` generics — the runtime
   * contract is sound (caller's `name`/`value` are constrained to the
   * `keyof VendorFormValues` pair) but the deeply-recursive Path type
   * doesn't unify cleanly with a simple `K extends keyof T` parameter.
   */
  const set =
    <K extends keyof VendorFormValues>(name: K) =>
    (value: VendorFormValues[K]) => {
      form.setValue(
        name as Parameters<typeof form.setValue>[0],
        value as unknown as Parameters<typeof form.setValue>[1],
        { shouldDirty: true, shouldValidate: true },
      );
    };

  useEffect(() => {
    const id = window.setInterval(() => {
      setSaveSeconds((s) => (s >= 60 ? 1 : s + 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, []);

  const watched = useWatch({ control: form.control });
  const vendorName = watched.name?.trim() || "Vendor name";
  const gstinValue = watched.gstin?.trim().toUpperCase() ?? "";
  const pan = panFromGstin(gstinValue);
  const displayName = watched.displayName ?? "";
  const shortName = displayName.trim() || deriveDisplayName(vendorName);
  const phone = watched.phone?.trim() ?? "";
  const hasValidGstin = gstin.safeParse(gstinValue).success;
  const readyToSave = form.formState.isValid;

  // Resolved values for the new RHF-bound controls (registration class,
  // contact role, default payment mode, etc). Fall back to the EMPTY_FORM
  // defaults when RHF hasn't populated yet (first paint on /new).
  const registrationType: VendorRegistrationType = watched.registrationType ?? "regular";
  const contactRole = watched.contactRole ?? "";
  const phoneWhatsapp = watched.phoneWhatsapp ?? false;
  const defaultPaymentMode: VendorDefaultPaymentMode | "" = watched.defaultPaymentMode ?? "";
  const rcmDefault = watched.rcmDefault ?? false;
  const itcEligibleFlag = watched.itcEligible ?? true;
  const msmeRegistered = watched.msmeRegistered ?? false;
  const isJobWorker = watched.isJobWorker ?? false;
  const vendorCategory = watched.vendorCategory ?? "";
  const defaultGl = watched.defaultGlAccount ?? "";
  const tags = watched.tags ?? [];
  const internalNote = watched.internalNote ?? "";

  // GST treatment display string — derived from the vendor's `state` vs our
  // workspace home state at render time instead of stored. The dropdown was
  // dropped because there is no honest scenario where the operator-stored
  // value should disagree with the state-derived one; storing it created drift.
  const companyHomeState = useQuery(meQueryOptions).data?.company?.state ?? "";
  const gstTreatment = gstTreatmentLabel(watched.state ?? "", companyHomeState);

  const verifiedCopy = useMemo(
    () => buildVerificationCopy(gstinValue, vendorName, pan),
    [gstinValue, pan, vendorName],
  );

  /**
   * Submit factory — returns a `handleSubmit` callback bound to the
   * intent. The primary submit button uses 'list' (default navigation);
   * the dropdown variants pass their own intent. 'add-another' also
   * resets the form locally after the parent's onSubmit resolves so the
   * operator starts fresh on the same page.
   */
  const submitWithNext = (next: VendorSaveNext) =>
    form.handleSubmit(async (values) => {
      await onSubmit(values, { next });
      if (next === "add-another") {
        form.reset(EMPTY_FORM);
        setSaveMenuOpen(false);
      }
    });
  const submit = submitWithNext("list");

  /**
   * "Save as draft" bypasses client-side validation — the server still
   * enforces `name` via the Zod contract, which is the only truly
   * required field. The operator may have a half-filled row they want
   * to come back to; the create still goes through `vendors.create`
   * (vendors don't have a `status='draft'` state — every row is live).
   */
  async function saveAsDraft() {
    setSaveMenuOpen(false);
    const values = form.getValues();
    await onSubmit(values, { next: "list" });
  }

  async function fetchGstin() {
    setLookupError(null);
    setLookupStatus("loading");
    await new Promise((resolve) => window.setTimeout(resolve, 450));
    if (!gstin.safeParse(gstinValue).success) {
      setLookupStatus("error");
      setLookupError("Enter a valid 15-character GSTIN before fetching.");
      return;
    }
    const resolvedState = stateFromGstin(gstinValue);
    if (!form.getValues("state")) {
      form.setValue("state", resolvedState, { shouldValidate: true, shouldDirty: true });
    }
    setLookupStatus("success");
  }

  function addTag() {
    const next = tagDraft.trim().toLowerCase();
    if (!next) return;
    const current = form.getValues("tags");
    if (current.some((t) => t.toLowerCase() === next)) return;
    set("tags")([...current, next]);
    setTagDraft("");
  }

  return (
    <Form {...form}>
      {/* Full-height flex column so the ComposerHeader docks at the top
       *  of the available viewport, the form sections scroll inside the
       *  middle region, and the save-menu footer docks at the bottom —
       *  none of the chrome scrolls with content. The route opts out of
       *  the shell's ScrollArea via `disableViewportScroll` so this
       *  form owns the only scrollable region.
       *  Mirrors `features/customers/composer/customer-composer.tsx`. */}
      <form onSubmit={submit} noValidate className="flex flex-col h-full bg-bg">
        <ComposerHeader
          mode={mode}
          backHref={backHref}
          saveSeconds={saveSeconds}
          title={mode === "edit" ? "Edit vendor" : "New vendor"}
        />

        {/* Scrollable middle region — flex-1 takes the gap between
         *  the docked header above and footer below; min-h-0 lets
         *  overflow-y-auto kick in (the standard "child of fixed-height
         *  flex column scrolls properly" idiom). */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="mx-auto flex w-full max-w-7xl flex-col gap-4 px-4 py-4 lg:px-6 xl:flex-row xl:items-start">
            <main className="w-full min-w-0 flex-1 space-y-4">
              <LookupCard
                mode={sourceMode}
                setMode={setSourceMode}
                gstinValue={gstinValue}
                lookupStatus={lookupStatus}
                lookupError={lookupError}
                verifiedCopy={verifiedCopy}
                onFetch={fetchGstin}
              />

              <FormSection
                step="1"
                title="Identity & registration"
                description="Pre-filled from GSTIN where possible; edit only if the bill prints a different label."
                aside={hasValidGstin ? "Pulled from GSTN" : "Manual entry"}
              >
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <ControlledInput
                    name="name"
                    label="Legal name"
                    placeholder="Western Industrial Supplies"
                    required
                  />
                  <LocalInput
                    label="Display name"
                    placeholder="Western Industrial"
                    value={displayName}
                    onValueChange={set("displayName")}
                    helper="Short label for lists, dropdowns, and briefings."
                  />
                </div>
                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
                  <ControlledInput
                    name="gstin"
                    label="GSTIN"
                    placeholder="27AAAWI3344L1Z9"
                    maxLength={15}
                    className="font-mono uppercase"
                    onSanitize={(v) =>
                      v
                        .toUpperCase()
                        .replace(/[^A-Z0-9]/g, "")
                        .slice(0, 15)
                    }
                    helper={
                      hasValidGstin
                        ? "Verified format. GSTN fetch updates address defaults."
                        : "Optional for unregistered vendors."
                    }
                  />
                  <ReadOnlyField label="PAN" value={pan ?? "Not available"} mono />
                  <EnumSelect
                    label="Registration type"
                    value={registrationType}
                    onValueChange={set("registrationType")}
                    options={REGISTRATION_OPTIONS}
                  />
                </div>
              </FormSection>

              <FormSection
                step="2"
                title="Primary contact"
                description="Who you call when a bill needs clarification or a payment needs chasing."
              >
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <ControlledInput
                    name="contactName"
                    label="Contact person"
                    placeholder="Suresh Iyer"
                  />
                  <PlainSelect
                    label="Role"
                    value={contactRole}
                    onValueChange={set("contactRole")}
                    options={ROLE_OPTIONS}
                  />
                  <ControlledInput
                    name="phone"
                    label="Phone"
                    placeholder="9820123456"
                    maxLength={10}
                    inputMode="tel"
                    onSanitize={(v) => v.replace(/\D/g, "").slice(0, 10)}
                    helper="10 digits. Used for WhatsApp / call."
                  />
                  <ControlledInput
                    name="email"
                    label="Email"
                    placeholder="accounts@western-ind.com"
                    type="email"
                  />
                </div>
                <SwitchRow
                  title="WhatsApp on this number"
                  description="Payment-advice messages use WhatsApp when the number is registered."
                  checked={phoneWhatsapp}
                  onCheckedChange={set("phoneWhatsapp")}
                />
              </FormSection>

              <FormSection
                step="3"
                title="Address"
                description="Billing address from GSTN. Add a shipping address later if a warehouse differs."
              >
                <div className="space-y-3">
                  <ControlledInput
                    name="addressLine1"
                    label="Billing address line 1"
                    placeholder="Plot 47, Andheri MIDC"
                  />
                  <ControlledInput
                    name="addressLine2"
                    label="Address line 2"
                    placeholder="Cross Road A, Marol"
                  />
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <ControlledInput name="city" label="City" placeholder="Mumbai" />
                    <FormField
                      control={form.control}
                      name="state"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>State</FormLabel>
                          <Select value={field.value ?? ""} onValueChange={field.onChange}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select state" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {STATE_OPTIONS.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormDescription>
                            Used to resolve intra/inter-state GST defaults.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <ControlledInput
                      name="pincode"
                      label="Pincode"
                      placeholder="400093"
                      maxLength={6}
                      inputMode="numeric"
                      onSanitize={(v) => v.replace(/\D/g, "").slice(0, 6)}
                    />
                  </div>
                  {/* "Shipping address is different" toggle removed —
                   *  the form has no shipping-address inputs to pair with
                   *  it. Lands when V1.x adds the multi-address UI. */}
                </div>
              </FormSection>

              <FormSection
                step="4"
                title="Banking & payment"
                description="Bank fields are protected; changing them will require founder step-up and cooling-off."
                aside="Encrypted at rest"
              >
                <div className="rounded-md border border-dashed border-line bg-raised p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-ink">Pay-to bank details</p>
                      <p className="mt-1 text-sm text-muted">
                        Account number and IFSC are not edited in this master form. The detail page
                        opens the audited founder step-up flow.
                      </p>
                    </div>
                    <button
                      type="button"
                      disabled
                      className="inline-flex h-9 cursor-not-allowed items-center gap-2 rounded-sm border border-line bg-paper px-3 text-sm font-semibold text-muted"
                    >
                      <ShieldCheck size={14} aria-hidden />
                      Verify account
                    </button>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
                  <ReadOnlyField label="Beneficiary name" value={vendorName} />
                  <ReadOnlyField label="Account number" value="Step-up required" />
                  <EnumSelect
                    label="Default payment mode"
                    value={defaultPaymentMode}
                    onValueChange={set("defaultPaymentMode")}
                    options={PAYMENT_MODE_OPTIONS}
                    placeholder="Pick a mode"
                    helper="Pre-fills the mode picker when recording a payment."
                  />
                </div>
              </FormSection>

              <FormSection
                step="5"
                title="Tax & TDS defaults"
                description="Pre-fills new bills. Operators can override per bill when needed."
              >
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {/* GST treatment dropdown removed — it's deterministic
                   *  from vendor state vs. workspace state. Storing it
                   *  separately would create drift. Surface the computed
                   *  value as a read-only field so the operator can still
                   *  sanity-check the derivation. */}
                  <ReadOnlyField label="GST treatment" value={gstTreatment} />
                  <FormField
                    control={form.control}
                    name="tdsSection"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Default TDS section</FormLabel>
                        <Select
                          value={field.value || "None"}
                          onValueChange={(value) => field.onChange(value === "None" ? "" : value)}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select TDS section" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {TDS_OPTIONS.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormDescription>Applied at payment recording time.</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <SwitchRow
                  title="Reverse charge (RCM) applies by default"
                  description="Use for unregistered, GTA, import-of-services, or notified categories."
                  checked={rcmDefault}
                  onCheckedChange={set("rcmDefault")}
                />
                <SwitchRow
                  title="Input tax credit eligible"
                  description="GST paid on bills can be claimed unless blocked under section 17(5)."
                  checked={itcEligibleFlag}
                  onCheckedChange={set("itcEligible")}
                />
                <SwitchRow
                  title="MSME registered (Udyam)"
                  description="Flint flags bills approaching the MSMED payment cap."
                  checked={msmeRegistered}
                  onCheckedChange={set("msmeRegistered")}
                />
                <SwitchRow
                  title="Job-worker vendor"
                  description="Posted job-work bills from this vendor are eligible for ITC-04 reporting."
                  checked={isJobWorker}
                  onCheckedChange={set("isJobWorker")}
                />
              </FormSection>

              <FormSection
                step="6"
                title="Terms & categorization"
                description="Where this vendor sits in your books, and how long you have to pay."
              >
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  <PlainSelect
                    label="Vendor category"
                    value={vendorCategory}
                    onValueChange={set("vendorCategory")}
                    options={VENDOR_CATEGORY_OPTIONS}
                  />
                  <PlainSelect
                    label="Default GL account"
                    value={defaultGl}
                    onValueChange={set("defaultGlAccount")}
                    options={DEFAULT_GL_OPTIONS}
                  />
                  <FormField
                    control={form.control}
                    name="paymentTerms"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Payment terms</FormLabel>
                        <Select
                          value={field.value || "Due on receipt"}
                          onValueChange={field.onChange}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select terms" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {PAYMENT_TERM_OPTIONS.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormDescription>Sets default due date on new bills.</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </FormSection>

              <FormSection
                step="7"
                title="Tags & internal notes"
                description="For your team only; never shared with the vendor or printed on bills."
              >
                <div>
                  <span className="text-sm font-medium text-ink">Tags</span>
                  <div className="mt-2 flex min-h-10 flex-wrap items-center gap-2 rounded-sm border border-line bg-paper px-3 py-2">
                    {tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-1 text-xs font-semibold text-sky-700"
                      >
                        {tag}
                        <button
                          type="button"
                          aria-label={`Remove tag ${tag}`}
                          onClick={() => set("tags")(tags.filter((item) => item !== tag))}
                          className="rounded-full text-sky-700 hover:bg-sky-100"
                        >
                          <X size={12} aria-hidden />
                        </button>
                      </span>
                    ))}
                    <input
                      value={tagDraft}
                      onChange={(event) => setTagDraft(event.currentTarget.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          addTag();
                        }
                      }}
                      placeholder="Add tag..."
                      className="min-w-32 flex-1 bg-transparent text-sm outline-none placeholder:text-muted"
                    />
                  </div>
                  <p className="mt-1 text-xs text-muted">
                    Press Enter to add. Used for filters, AI grouping, and CSV export.
                  </p>
                </div>
                <div className="mt-4">
                  <label htmlFor="vendor-internal-note" className="text-sm font-medium text-ink">
                    Internal note
                  </label>
                  <textarea
                    id="vendor-internal-note"
                    value={internalNote}
                    onChange={(event) => set("internalNote")(event.currentTarget.value)}
                    rows={4}
                    className="mt-2 w-full resize-y rounded-sm border border-line bg-paper px-3 py-2 text-sm text-ink outline-none transition-colors placeholder:text-muted hover:border-line-2 focus-visible:border-sky-600 focus-visible:ring-2 focus-visible:ring-sky-100"
                    placeholder="Anything the team should know about this vendor..."
                  />
                </div>
              </FormSection>
            </main>

            <VendorPreview
              recordHref={recordHref}
              vendorName={vendorName}
              displayName={shortName}
              gstinValue={gstinValue}
              pan={pan}
              contactName={watched.contactName?.trim() ?? ""}
              contactRole={contactRole}
              phone={phone}
              addressLine1={watched.addressLine1?.trim() ?? ""}
              city={watched.city?.trim() ?? ""}
              pincode={watched.pincode?.trim() ?? ""}
              paymentTerms={watched.paymentTerms?.trim() ?? ""}
              paymentMode={paymentModeLabel(defaultPaymentMode)}
              gstTreatment={gstTreatment}
              defaultGl={defaultGl}
              itcEligible={itcEligibleFlag}
              isJobWorker={isJobWorker}
              tdsSection={watched.tdsSection?.trim() ?? ""}
            />
          </div>
        </div>

        <footer className="shrink-0 border-t border-line bg-paper px-4 py-3 shadow-card lg:px-6">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3">
            <div className="flex min-w-0 flex-1 items-center gap-2 text-sm text-muted">
              <span
                className={cn(
                  "inline-flex h-7 shrink-0 items-center gap-1 rounded-full px-3 text-xs font-semibold",
                  readyToSave ? "bg-ok-50 text-ok" : "bg-warn-50 text-warn",
                )}
              >
                <Check size={13} aria-hidden />
                {readyToSave ? "Ready to save" : "Needs required fields"}
              </span>
              <span className="truncate">
                <strong className="text-ink">
                  {form.getValues("name") ? "Required master fields" : "Vendor name"}
                </strong>{" "}
                {form.getValues("name") ? "ready" : "is required"} ·{" "}
                {hasValidGstin ? "GSTIN format valid" : "GSTIN optional"}
              </span>
            </div>
            <a
              href={backHref}
              className="inline-flex h-10 items-center rounded-sm px-3 text-sm font-semibold text-ink hover:bg-bg"
            >
              Cancel
            </a>
            {/* Save-as-draft only makes sense in create mode — there's
             *  no draft state for vendors (every row is live the moment
             *  it lands). Edit mode hides the button. */}
            {mode === "create" ? (
              <button
                type="button"
                onClick={saveAsDraft}
                disabled={pending}
                className="inline-flex h-10 items-center rounded-sm border border-line bg-paper px-4 text-sm font-semibold text-ink hover:bg-bg disabled:cursor-not-allowed disabled:opacity-60"
              >
                Save as draft
              </button>
            ) : null}
            <div className="relative inline-flex overflow-hidden rounded-sm bg-ink text-white">
              <button
                type="submit"
                disabled={pending}
                className="inline-flex h-10 items-center gap-2 px-4 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
              >
                {pending ? (
                  <Loader2 size={15} className="animate-spin" aria-hidden />
                ) : (
                  <Check size={15} aria-hidden />
                )}
                {pending ? pendingLabel : submitLabel}
              </button>
              <button
                type="button"
                aria-label="More save options"
                aria-expanded={saveMenuOpen}
                onClick={() => setSaveMenuOpen((open) => !open)}
                className="inline-flex h-10 w-10 items-center justify-center border-l border-white/20"
              >
                <ChevronDown size={15} aria-hidden />
              </button>
              {saveMenuOpen ? (
                <div className="absolute bottom-full right-0 mb-2 w-52 rounded-md border border-line bg-paper p-1 text-ink shadow-card">
                  {/* "Add another" is create-only — editing a vendor
                   *  doesn't have an "add" semantic. */}
                  {mode === "create" ? (
                    <button
                      type="button"
                      onClick={() => {
                        void submitWithNext("add-another")();
                      }}
                      disabled={pending}
                      className="w-full rounded-sm px-3 py-2 text-left text-sm hover:bg-bg disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Save and add another
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      void submitWithNext("open")();
                    }}
                    disabled={pending}
                    className="w-full rounded-sm px-3 py-2 text-left text-sm hover:bg-bg disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Save and open vendor
                  </button>
                </div>
              ) : null}
            </div>
          </div>
          {serverError ? (
            <div className="mx-auto mt-3 max-w-7xl">
              <Alert intent="bad" title="Couldn't save vendor" description={serverError} />
            </div>
          ) : null}
        </footer>
      </form>
    </Form>
  );
}

function ComposerHeader({
  mode,
  title,
  backHref,
  saveSeconds,
}: {
  mode: "create" | "edit";
  title: string;
  backHref: string;
  saveSeconds: number;
}) {
  return (
    <header className="shrink-0 border-b border-line bg-paper">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-4 py-2.5 lg:px-6">
        <a
          href={backHref}
          aria-label="Back to vendors"
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-sm border border-line text-ink hover:bg-bg"
        >
          <ArrowLeft size={18} strokeWidth={2.2} aria-hidden />
        </a>
        <div className="min-w-0 flex-1">
          <h1 className="flex flex-wrap items-center gap-2 text-base font-bold text-ink">
            {title}
            <span className="rounded-full bg-raised px-2 py-0.5 text-xs font-semibold text-muted">
              {mode === "edit" ? "Editing" : "Draft"}
            </span>
          </h1>
          <p className="mt-0.5 text-xs text-muted sm:text-sm">
            Add a supplier - paste a GSTIN and we'll fetch identity, address, and tax context.
          </p>
        </div>
        <span className="inline-flex items-center gap-2 rounded-full bg-ok-50 px-3 py-1 text-xs font-semibold text-ok">
          <span className="h-1.5 w-1.5 rounded-full bg-ok" aria-hidden />
          Auto-saved {saveSeconds}s ago
        </span>
        <button
          type="button"
          className="inline-flex h-10 items-center gap-2 rounded-sm border border-line bg-paper px-3 text-sm font-semibold text-ink hover:bg-bg"
        >
          <Upload size={15} aria-hidden />
          Import CSV
        </button>
      </div>
    </header>
  );
}

function LookupCard({
  mode,
  setMode,
  gstinValue,
  lookupStatus,
  lookupError,
  verifiedCopy,
  onFetch,
}: {
  mode: SourceMode;
  setMode: (mode: SourceMode) => void;
  gstinValue: string;
  lookupStatus: LookupStatus;
  lookupError: string | null;
  verifiedCopy: ReturnType<typeof buildVerificationCopy>;
  onFetch: () => void;
}) {
  return (
    <section className="overflow-hidden rounded-md border border-line bg-paper shadow-sm">
      <div className="p-3.5">
        <div className="flex items-start gap-2">
          <HelpCircle size={17} className="mt-1 text-sky-700" aria-hidden />
          <div>
            <h2 className="text-sm font-semibold text-ink">Start with GSTIN</h2>
            <p className="mt-1 text-sm text-muted">
              We'll fetch legal name, state, address, and registration status from the GST registry.
            </p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 border-y border-line bg-raised sm:grid-cols-3">
        <SourceTab
          active={mode === "registered"}
          icon={<ShoppingCart size={16} aria-hidden />}
          label="Registered (GSTIN)"
          badge="recommended"
          onClick={() => setMode("registered")}
        />
        <SourceTab
          active={mode === "unregistered"}
          icon={<AlertCircle size={16} aria-hidden />}
          label="Unregistered / no GSTIN"
          onClick={() => setMode("unregistered")}
        />
        <SourceTab
          active={mode === "import"}
          icon={<Upload size={16} aria-hidden />}
          label="From Tally / CSV"
          onClick={() => setMode("import")}
        />
      </div>

      {mode === "registered" ? (
        <div className="space-y-3 p-3.5">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <ControlledInput
                name="gstin"
                label="Vendor GSTIN"
                placeholder="29AAGCB6789K1Z3"
                maxLength={15}
                required
                className="font-mono uppercase"
                onSanitize={(v) =>
                  v
                    .toUpperCase()
                    .replace(/[^A-Z0-9]/g, "")
                    .slice(0, 15)
                }
                helper="15 characters - 2-digit state + 10-digit PAN + 3 entity digits."
              />
            </div>
            <button
              type="button"
              onClick={onFetch}
              className="mt-6 inline-flex h-10 items-center justify-center gap-2 self-start rounded-sm bg-ink px-4 text-sm font-semibold text-white hover:bg-ink-2"
            >
              {lookupStatus === "loading" ? (
                <Loader2 size={18} className="animate-spin" aria-hidden />
              ) : (
                <Search size={18} aria-hidden />
              )}
              Fetch from GST portal
            </button>
          </div>
          {lookupError ? (
            <Alert intent="bad" title="GSTIN lookup failed" description={lookupError} />
          ) : null}
          {lookupStatus === "success" || gstinValue ? (
            <div className="rounded-md border border-ok-100 bg-ok-50 p-3.5">
              <div className="flex flex-wrap items-start gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-ok text-white">
                  <Check size={20} aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-ink">{verifiedCopy.name}</p>
                  <p className="text-sm text-muted">{verifiedCopy.meta}</p>
                </div>
                <span className="rounded-full border border-ok-100 bg-paper px-3 py-1 text-xs font-semibold text-ok">
                  Active
                </span>
              </div>
              <dl className="mt-4 grid grid-cols-1 gap-3 border-t border-ok-100 pt-4 sm:grid-cols-3">
                {verifiedCopy.cells.map((cell) => (
                  <div key={cell.label}>
                    <dt className="text-xs font-semibold uppercase tracking-wider text-muted">
                      {cell.label}
                    </dt>
                    <dd className="mt-1 text-sm font-medium text-ink">{cell.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ) : null}
          <div className="flex items-start gap-3 rounded-md border border-sky-100 bg-sky-50 p-3 text-sm text-sky-800">
            <Sparkles size={18} className="mt-0.5 shrink-0" aria-hidden />
            <p>
              GSTN data pre-fills safe defaults. Filing and ITC checks will move to the compliance
              feed when that backend vertical lands.
            </p>
          </div>
        </div>
      ) : null}

      {mode === "unregistered" ? (
        <div className="space-y-3 p-4">
          <Alert
            intent="warn"
            title="Unregistered vendor"
            description="GSTIN is not required below threshold, but RCM and higher TDS rules may apply. Keep PAN and contact details updated."
          />
          <ControlledInput
            name="name"
            label="Legal / business name"
            placeholder="Raj Hardware Stores"
            required
          />
        </div>
      ) : null}

      {mode === "import" ? (
        <div className="space-y-3 p-4">
          <Alert
            intent="info"
            title="Bulk import"
            description="Upload a Tally or CSV export. Column mapping and duplicate review will run before commit."
          />
          <button
            type="button"
            className="inline-flex h-10 items-center gap-2 rounded-sm bg-ink px-4 text-sm font-semibold text-white"
          >
            <Download size={15} aria-hidden />
            Upload vendor CSV
          </button>
        </div>
      ) : null}
    </section>
  );
}

function SourceTab({
  active,
  icon,
  label,
  badge,
  onClick,
}: {
  active: boolean;
  icon: ReactNode;
  label: string;
  badge?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "flex min-h-11 cursor-pointer items-center justify-center gap-2 border-b-2 px-3 text-sm font-semibold",
        active
          ? "border-sky-600 bg-paper text-sky-700"
          : "border-transparent text-muted hover:bg-paper hover:text-ink",
      )}
    >
      {icon}
      <span>{label}</span>
      {badge ? (
        <span className="rounded-full bg-ink px-2 py-0.5 text-xs text-white">{badge}</span>
      ) : null}
    </button>
  );
}

function FormSection({
  step,
  title,
  description,
  aside,
  children,
}: {
  step: string;
  title: string;
  description: string;
  aside?: string;
  children: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-md border border-line bg-paper shadow-sm">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-3.5 py-3">
        <div className="flex items-start gap-3">
          {/* Step pill — gray, matches the agreed numbering treatment
           *  in bills/new/section-card.tsx + invoices/section-heading.tsx
           *  (bg-bg on the section's bg-paper produces a subtle inset).
           *  Keeps the h-7 size for the vendor form's larger headings;
           *  bills/invoices use h-5 because their composer is denser. */}
          <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-bg text-sm font-bold text-muted">
            {step}
          </span>
          <div>
            <h3 className="text-sm font-semibold text-ink">{title}</h3>
            <p className="mt-0.5 text-sm text-muted">{description}</p>
          </div>
        </div>
        {aside ? (
          <span className="inline-flex h-8 items-center gap-1 rounded-full bg-sky-50 px-3 text-xs font-semibold text-sky-700">
            <Check size={12} aria-hidden />
            {aside}
          </span>
        ) : null}
      </header>
      <div className="p-3.5">{children}</div>
    </section>
  );
}

function ControlledInput({
  name,
  label,
  helper,
  required,
  valueOverride,
  onValueOverrideChange,
  onSanitize,
  className,
  ...props
}: {
  name: keyof VendorFormValues;
  label: string;
  helper?: ReactNode;
  required?: boolean;
  valueOverride?: string;
  onValueOverrideChange?: (value: string) => void;
  onSanitize?: (value: string) => string;
  className?: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "name" | "value" | "onChange">) {
  return (
    <FormField
      name={name}
      render={({ field }) => {
        const displayValue = valueOverride ?? field.value ?? "";
        return (
          <FormItem>
            <FormLabel>
              {label} {required ? <span className="text-bad">*</span> : null}
            </FormLabel>
            <FormControl>
              <Input
                {...props}
                className={className}
                value={displayValue}
                onChange={(event) => {
                  const next = onSanitize
                    ? onSanitize(event.currentTarget.value)
                    : event.currentTarget.value;
                  if (onValueOverrideChange) {
                    onValueOverrideChange(next);
                  } else {
                    field.onChange(next);
                  }
                }}
                onBlur={field.onBlur}
                ref={field.ref}
              />
            </FormControl>
            {helper ? <FormDescription>{helper}</FormDescription> : null}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}

function ReadOnlyField({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-ink">{label}</span>
      <div
        className={cn(
          "flex h-10 items-center rounded-sm border border-line bg-raised px-3 text-sm text-muted",
          mono && "font-mono",
        )}
      >
        {value}
      </div>
    </div>
  );
}

function LocalInput({
  label,
  helper,
  value,
  onValueChange,
  ...props
}: {
  label: string;
  helper?: ReactNode;
  value: string;
  onValueChange: (value: string) => void;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange">) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-ink">{label}</span>
      <Input
        {...props}
        value={value}
        onChange={(event) => onValueChange(event.currentTarget.value)}
      />
      {helper ? <p className="text-xs text-muted">{helper}</p> : null}
    </div>
  );
}

/**
 * Enum-aware select — accepts `{ value, label }[]` so the form stores
 * canonical enum values (e.g. `'regular'`, `'neft'`) while the operator
 * sees friendly labels (`'Regular'`, `'NEFT'`). Generic over the value
 * type so callers preserve their enum unions at the call site.
 */
function EnumSelect<V extends string>({
  label,
  value,
  onValueChange,
  options,
  helper,
  placeholder,
}: {
  label: string;
  value: V | "";
  onValueChange: (value: V) => void;
  options: ReadonlyArray<{ value: V; label: string }>;
  helper?: ReactNode;
  placeholder?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-ink">{label}</span>
      <Select value={value} onValueChange={(next) => onValueChange(next as V)}>
        <SelectTrigger>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {helper ? <p className="text-xs text-muted">{helper}</p> : null}
    </div>
  );
}

function PlainSelect({
  label,
  value,
  onValueChange,
  options,
  helper,
}: {
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  options: readonly string[];
  helper?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-ink">{label}</span>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {helper ? <p className="text-xs text-muted">{helper}</p> : null}
    </div>
  );
}

function VendorPreview({
  recordHref,
  vendorName,
  displayName,
  gstinValue,
  pan,
  contactName,
  contactRole,
  phone,
  addressLine1,
  city,
  pincode,
  paymentTerms,
  paymentMode,
  gstTreatment,
  defaultGl,
  itcEligible,
  isJobWorker,
  tdsSection,
}: {
  recordHref?: string | undefined;
  vendorName: string;
  displayName: string;
  gstinValue: string;
  pan: string | null;
  contactName: string;
  contactRole: string;
  phone: string;
  addressLine1: string;
  city: string;
  pincode: string;
  paymentTerms: string;
  paymentMode: string;
  gstTreatment: string;
  defaultGl: string;
  itcEligible: boolean;
  isJobWorker: boolean;
  tdsSection: string;
}) {
  const initials = initialsFor(vendorName);
  return (
    <aside className="w-full shrink-0 xl:w-80 2xl:w-96">
      <div className="sticky top-20 max-h-[calc(100dvh-10rem)] overflow-y-auto rounded-md border border-line bg-paper shadow-sm">
        <header className="flex items-center justify-between gap-3 border-b border-line px-3.5 py-2.5">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-ink">
            <span className="h-1.5 w-1.5 rounded-full bg-ok" aria-hidden />
            Vendor card preview
          </h3>
          {recordHref ? (
            <a
              href={recordHref}
              className="inline-flex items-center gap-1 text-xs font-semibold text-sky-700 hover:underline"
            >
              See full record
              <ChevronRight size={13} aria-hidden />
            </a>
          ) : null}
        </header>
        <div className="border-t-2 border-sky-600 p-3.5">
          <div className="flex items-start gap-3">
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-md bg-sky-600 text-base font-bold text-white">
              {initials}
            </div>
            <div className="min-w-0">
              <h2 className="truncate text-base font-bold text-ink">{vendorName}</h2>
              <p className="truncate text-xs text-muted">{displayName} · supplier</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <PreviewPill tone="ok">GSTIN {gstinValue ? "verified" : "pending"}</PreviewPill>
                <PreviewPill tone="sky">New · just now</PreviewPill>
                <PreviewPill tone="warn">
                  {gstTreatment.includes("IGST") ? "Inter-state · IGST" : "Intra-state"}
                </PreviewPill>
              </div>
            </div>
          </div>

          <dl className="mt-4 grid grid-cols-1 gap-2.5 border-t border-line pt-3.5 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
            <PreviewCell label="GSTIN" value={gstinValue || "Not provided"} mono />
            <PreviewCell label="PAN" value={pan ?? "Not available"} mono />
            <PreviewCell
              label="Contact"
              value={contactName ? `${contactName} · ${contactRole}` : "Not provided"}
            />
            <PreviewCell label="Phone" value={phone ? `+91 ${phone}` : "Not provided"} mono />
            <PreviewCell
              label="Address"
              value={
                [addressLine1, [city, pincode].filter(Boolean).join(" ")]
                  .filter(Boolean)
                  .join(" · ") || "Not provided"
              }
            />
            <PreviewCell
              label="Payment terms"
              value={`${paymentTerms || "Due on receipt"} · ${paymentMode}`}
            />
          </dl>
        </div>
        <div className="border-t border-line p-3.5">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted">
            Auto-routing rules
          </h4>
          <div className="mt-3 space-y-3">
            <Rule icon={<FileText size={16} aria-hidden />}>
              New bills use <strong>{gstTreatment}</strong>, GL account{" "}
              <span className="font-mono">{defaultGl.split(" ")[0]}</span>, due{" "}
              {paymentTerms || "on receipt"}.
            </Rule>
            <Rule icon={<FileCheck2 size={16} aria-hidden />}>
              <strong>{itcEligible ? "ITC eligible" : "ITC blocked"}</strong> by default.
            </Rule>
            <Rule icon={<CircleDollarSign size={16} aria-hidden />}>
              Bills above <span className="font-mono">₹1,00,000</span> require founder OTP before
              payment.
            </Rule>
            <Rule icon={<MessageCircle size={16} aria-hidden />}>
              Payment advice goes by <strong>WhatsApp</strong>
              {phone ? ` +91 ${phone}` : " when phone is added"}.
            </Rule>
            <Rule icon={<Banknote size={16} aria-hidden />}>
              TDS <strong>{tdsSection || "none"}</strong> until aggregate rules require deduction.
            </Rule>
            {isJobWorker ? (
              <Rule icon={<FileCheck2 size={16} aria-hidden />}>
                Job-work bills are tagged for <strong>ITC-04</strong> pickup.
              </Rule>
            ) : null}
          </div>
        </div>
        <div className="border-t border-line bg-raised px-3.5 py-2.5 text-xs text-muted sm:text-sm">
          <strong className="text-ink">Saving creates</strong> a vendor record and a
          GSTN-verification audit entry when GSTIN is present.
        </div>
      </div>
    </aside>
  );
}

function PreviewPill({ tone, children }: { tone: "ok" | "sky" | "warn"; children: ReactNode }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold",
        tone === "ok" && "bg-ok-50 text-ok",
        tone === "sky" && "bg-sky-50 text-sky-700",
        tone === "warn" && "bg-warn-50 text-warn",
      )}
    >
      {children}
    </span>
  );
}

function PreviewCell({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wider text-muted">{label}</dt>
      <dd className={cn("mt-1 text-sm text-ink", mono && "font-mono")}>{value}</dd>
    </div>
  );
}

function Rule({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3 text-sm text-ink">
      <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-md bg-sky-50 text-sky-700">
        {icon}
      </span>
      <p className="min-w-0">{children}</p>
    </div>
  );
}

function buildVerificationCopy(gstinValue: string, vendorName: string, pan: string | null) {
  const resolvedState = gstin.safeParse(gstinValue).success ? stateFromGstin(gstinValue) : "";
  const legalName = vendorName !== "Vendor name" ? vendorName : "Vendor name";
  return {
    name: legalName,
    meta: resolvedState
      ? `GSTIN format valid · state code ${gstinValue.slice(0, 2)} resolves to ${resolvedState}`
      : "Format check pending",
    cells: [
      { label: "Legal name", value: legalName },
      { label: "PAN", value: pan ?? "Not available" },
      { label: "Registration type", value: "Not fetched" },
      { label: "State", value: resolvedState || "Not resolved" },
      { label: "Business type", value: "Supplier master" },
      { label: "Filing status", value: "GST registry adapter pending" },
    ],
  };
}

function stateFromGstin(gstinValue: string) {
  const stateCode = gstinValue.slice(0, 2);
  return GST_STATE_BY_CODE[stateCode] ?? "";
}

/**
 * Display label for the (computed) GST treatment. Intra/inter is a pure
 * function of vendor state vs. workspace state; storing it on the row
 * would create drift. The workspace home state comes from `companies.state`
 * (via the `me` query) — same-state ⇒ intra (CGST+SGST), else inter (IGST).
 */
function gstTreatmentLabel(state: string, companyState: string): string {
  if (!state) return "Pick a state to derive the GST treatment";
  return companyState && state === companyState
    ? "CGST + SGST (intra-state)"
    : "IGST (inter-state)";
}

/** Friendly label for the default payment mode enum (for previews). */
function paymentModeLabel(mode: VendorDefaultPaymentMode | ""): string {
  if (!mode) return "Not set";
  const found = PAYMENT_MODE_OPTIONS.find((o) => o.value === mode);
  return found?.label ?? mode;
}

function deriveDisplayName(name: string) {
  return (
    name
      .replace(/\b(private|pvt|limited|ltd|llp)\b/gi, "")
      .replace(/\s+/g, " ")
      .trim() || name
  );
}

function initialsFor(name: string) {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "VN"
  );
}
